// src/components/PlanGate.jsx
// Wrap any page/section with this instead of hard-redirecting when a
// feature isn't in the user's plan. Showing what they're missing (with
// a clear upgrade CTA) converts better than a silent redirect or a
// hidden nav item — the person needs to see the locked feature to want it.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { planForFeature, FEATURE_MIN_PLAN, PLAN_LABELS, PLAN_PRICE_LABEL } from '../config/plans';

const FEATURE_PITCH = {
  tasks:{
    title: 'Task Tracking',
    body: 'Manage your Task Assigments from one to another person in the whole organization',
  },
  visits_expenses: {
    title: 'Visits & Expenses',
    body: 'Track field visits with GPS-verified routes, capture customer expectations, and run a manager → finance approval chain on every claim.',
  },
  call_records: {
    title: 'Calls',
    body: 'Keep call records, and keep a proper followup on which count in customer history too.',
  },
  followups: {
    title: 'Follow-ups Tracker',
    body: 'Never lose a lead to a forgotten follow-up — see everything due today and everything overdue, in one place.',
  },
  reports: {
    title: 'Reports & Analytics',
    body: 'Revenue trends, lead pipeline breakdowns, and rep performance — all in one dashboard.',
  },
  ocr: {
    title: 'Business Card Scanner',
    body: 'Turn a photo of a business card into a customer record in seconds.',
  },
  company_branding_edit: {
    title: 'Custom Branding',
    body: 'Put your own logo, bank details, and office addresses on every quote PDF you send.',
  },
  multi_level_approval: {
    title: 'Manager → Finance Approval',
    body: 'Add a real approval chain so nothing gets invoiced or paid out without sign-off.',
  },
  multi_branch: {
    title: 'Multiple Branches',
    body: 'Run reporting and approvals across more than one office location.',
  },
  reports_export: {
    title: 'Report Exports',
    body: 'Download reports as CSV/PDF for board meetings and audits.',
  },
  white_label: {
    title: 'White-label PDFs',
    body: 'Remove the platform footer from every quote and invoice you send.',
  },
};

export default function PlanGate({ feature, children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const unlocked = planForFeature(user?.plan, feature);

  if (unlocked) return children;

  const requiredPlan = FEATURE_MIN_PLAN[feature] || 'growth';
  const pitch = FEATURE_PITCH[feature] || { title: 'This feature', body: 'is part of a higher plan.' };

  return (
    <div className="page">
      <div className="card" style={{ maxWidth: 520, margin: '3rem auto', textAlign: 'center', padding: '2.2rem 1.8rem' }}>
        <div style={{ fontSize: '2.4rem', marginBottom: '.6rem' }}>🔒</div>
        <div style={{ fontWeight: 700, fontSize: '1.15rem', marginBottom: '.5rem' }}>{pitch.title}</div>
        <p style={{ color: 'var(--muted)', fontSize: '.9rem', marginBottom: '1.4rem' }}>{pitch.body}</p>
        <div style={{ background: 'var(--bg)', borderRadius: 10, padding: '.9rem 1.2rem', marginBottom: '1.4rem' }}>
          <div style={{ fontSize: '.78rem', color: 'var(--faint)' }}>Available on</div>
          <div style={{ fontWeight: 700, color: 'var(--accent)' }}>{PLAN_LABELS[requiredPlan]} plan</div>
          <div style={{ fontSize: '.82rem', color: 'var(--muted)' }}>{PLAN_PRICE_LABEL[requiredPlan]}</div>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/upgrade')} style={{ marginRight: 8 }}>
          Upgrade Plan
        </button>
        <button className="btn btn-ghost" onClick={() => navigate('/')}>Back to Dashboard</button>
      </div>
    </div>
  );
}
